import { AccountConnections } from "../components/AccountConnections";
import { LanguageSelector } from "../components/LanguageSelector";
import { t as msg } from "../lib/i18n";
import { useEffect, useState } from "react";
import { UserRound, LogOut, Check, ArrowRight } from "lucide-react";
import type { User } from "../../../../packages/shared/src/types";
import { api } from "../lib/api";
import { profileName, contactLabel } from "../lib/account";
import { getConfig, type Config } from "../components/Challenge";
import { DataTools, DeletedData } from "../components/DataTools";
export function Profile({
  user,
  signIn,
  reloadUser,
  signOut,
  signingOut,
  onDeleted,
}: {
  user: User | null;
  signIn: () => void;
  reloadUser: () => Promise<void>;
  signOut: () => void;
  signingOut: boolean;
  onDeleted: () => void;
}) {
  const [name, setName] = useState(user?.displayName || ""),
    [signature, setSignature] = useState(user?.defaultSignature || ""),
    [busy, setBusy] = useState(""),
    [error, setError] = useState(""),
    [saved, setSaved] = useState(false),
    [localCleanupFailed, setLocalCleanupFailed] = useState(false),
    [deleted, setDeleted] = useState(false),
    [features, setFeatures] = useState<Config["features"]>(),
    [configError, setConfigError] = useState("");
  async function loadMethods() {
    setConfigError("");
    try {
      setFeatures((await getConfig()).features);
    } catch {
      setConfigError(msg("Sign-in methods could not load."));
    }
  }
  useEffect(() => {
    if (user) void loadMethods();
  }, [user?.id]);
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy("profile");
    setError("");
    setSaved(false);
    try {
      await api("/api/privacy/profile", "POST", {
        displayName: name,
        signature,
        expectedUserId: user?.id,
      });
      setSaved(true);
      await reloadUser().catch(() => {});
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy("");
    }
  }
  async function connect(provider: "google" | "apple") {
    setBusy(provider);
    setError("");
    try {
      const result = await api<{ url: string }>(
        `/api/auth/${provider}/start`,
        "POST",
        { ageConfirmed: true, linkAccount: true, returnTo: "/profile" },
      );
      location.assign(result.url);
    } catch (e) {
      setError((e as Error).message);
      setBusy("");
    }
  }
  return (
    <section className="wrap section profile-page">
      <LanguageSelector user={user} />
      <div className="section-heading">
        <div>

          <h1 className="page-title">{msg("Profile.")}</h1>
        </div>
        <a className="text-link" href="/cards">
          {msg("My cards")}<ArrowRight size={16} />
        </a>
      </div>
      {deleted ? (
        <DeletedData localCleanupFailed={localCleanupFailed} />
      ) : !user ? (
        <div className="empty">
          <UserRound size={36} strokeWidth={1.3} />
          <h2>{msg("Keep the good things together.")}</h2>
          <p>
            {msg("Sign in to manage your profile and use the same account on the web and in the app.")}</p>
          <button className="button" onClick={signIn}>
            {msg("Sign in")}</button>
        </div>
      ) : (
        <>
          <div className="profile-identity">
            <span className="profile-avatar" aria-hidden="true">
              <UserRound size={28} strokeWidth={1.5} />
            </span>
            <div>
              <h2>{profileName(user)}</h2>
              <p>{contactLabel(user)}</p>
            </div>
            <span className="profile-plan">{msg("Free account")}</span>
          </div>
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          <p>
            <a className="text-link" href="/friends">
              {msg("Your friends")}<ArrowRight size={16} />
            </a>
          </p>
          <div className="profile-columns">
            <section
              className="profile-section"
              aria-labelledby={"profile-details-title"}
            >

              <h2 id="profile-details-title">{msg("Your details.")}</h2>
              <form onSubmit={save}><fieldset disabled={Boolean(busy)} className="form-fields">
                <label>
                  {msg("Display name")}<small>{msg("optional")}</small>
                  <input
                    autoComplete="nickname"
                    maxLength={60}
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setSaved(false);
                    }}
                  />
                </label>
                <label>
                  {msg("Default card signature")}<small>{msg("optional")}</small>
                  <input
                    maxLength={30}
                    value={signature}
                    onChange={(e) => {
                      setSignature(e.target.value);
                      setSaved(false);
                    }}
                  />
                </label>
                <p className="muted">
                  {msg("Your profile is shared across web and app. Cards already sent keep their original signature.")}</p>
                <div className="profile-save">
                  <button className="button" disabled={!!busy}>
                    {busy === "profile" ? msg("Saving…") : msg("Save profile")}
                  </button>
                  {saved && (
                    <span role="status">
                      <Check size={16} /> {msg("Saved")}</span>
                  )}
                </div>
              </fieldset></form>
            </section>
            <section
              className="profile-section"
              aria-labelledby={"profile-methods-title"}
            >

              <h2 id="profile-methods-title">{msg("Sign-in & access.")}</h2>
              <p>
                {msg("Add another way to sign in to this account. Connecting a provider keeps your cards here and does not merge separate accounts.")}</p>
              {!features && !configError && (
                <p role="status">{msg("Loading sign-in methods…")}</p>
              )}
              {configError && (
                <p role="alert">
                  {configError}{" "}
                  <button
                    className="text-button"
                    onClick={() => void loadMethods()}
                  >
                    {msg("Try again")}</button>
                </p>
              )}
              <div className="profile-methods">
                {(["google", "apple"] as const).map((provider) => {
                  const connected = user.connectedProviders?.includes(provider),
                    enabled =
                      features?.[
                        provider === "google" ? "googleLogin" : "appleLogin"
                      ];
                  if (!connected && !enabled) return null;
                  return (
                    <div className="profile-method" key={provider}>
                      <strong>
                        {provider === "google" ? "Google" : "Apple"}
                      </strong>
                      {connected ? (
                        <span>
                          <Check size={15} /> {msg("Connected")}</span>
                      ) : (
                        <button
                          className="text-button"
                          disabled={!!busy}
                          onClick={() => void connect(provider)}
                        >
                          {msg("Connect")}</button>
                      )}
                    </div>
                  );
                })}
                {user.email && (
                  <div className="profile-method">
                    <strong>{msg("Email link")}</strong>
                    <span>{msg("Available")}</span>
                  </div>
                )}
              </div>
              <p className="muted">
                {msg("Signing out clears this browser’s account access and access to linked guest cards. Cards remain on the server; other devices stay signed in.")}</p>
              <button
                className="button secondary"
                disabled={signingOut || !!busy}
                onClick={signOut}
              >
                <LogOut size={17} /> {signingOut ? msg("Signing out…") : msg("Sign out")}
              </button>
            </section>
          </div>
          <section id="connections"><AccountConnections key={user.id} user={user} reloadUser={reloadUser} /></section>
          <DataTools
            signIn={signIn}
            scope="account"
            ownerId={user.id}
            onDeleted={(failed) => {
              setLocalCleanupFailed(failed);
              setDeleted(true);
              onDeleted();
            }}
          />
        </>
      )}
    </section>
  );
}
